import type {
  AutomationCurve,
  AutomationLane,
  AutomationTarget,
  Clip,
  Marker,
  Project,
} from '@/domain/types';
import { resolveParamDescriptor } from '@/engine/automation';
import { insertClips, removeClips } from '@/domain/mutate';
import { clipEndTicks } from '@/domain/selectors';
import { sliceClip, subtractRange } from '@/domain/slice';
import { newClipId, newLaneId, newPointId } from '@/util/id';
import { defineCommand } from './registry';
import { parseClipValue } from '@/domain/format/parse';
import { boolParam, enumParam, idsParam, intParam, numberParam } from './params';

/**
 * Editing by time range.
 *
 * Clip editing answers "change this object"; range editing answers "change this
 * stretch of the piece" — take these two bars out and close up, make room for
 * four more, keep only the chorus. Those are the edits that change an
 * arrangement rather than a sound, and they are the reason a timeline has a
 * ruler rather than just a list of clips.
 *
 * Every operation here is built from `sliceClip` and `subtractRange`, the same
 * two functions splitting and comping use. Splicing is not a new kind of edit;
 * it is cutting at two points and moving what is left.
 */

const MAX_TRACKS = 512;

interface RangeTarget {
  trackIds: string[];
  startTicks: number;
  endTicks: number;
}

function parseRange(raw: Record<string, unknown>): RangeTarget | null {
  const trackIds = idsParam(raw.trackIds, MAX_TRACKS);
  const startTicks = intParam(raw.startTicks, 0, Number.MAX_SAFE_INTEGER);
  const endTicks = intParam(raw.endTicks, 0, Number.MAX_SAFE_INTEGER);
  if (!trackIds || trackIds.length === 0 || startTicks === null || endTicks === null) return null;
  if (endTicks <= startTicks) return null;
  return { trackIds, startTicks, endTicks };
}

/** The clips of the named tracks, comp lane and take lanes alike. */
function clipsOfTracks(project: Project, trackIds: readonly string[]): Clip[] {
  const wanted = new Set(trackIds);
  return Object.values(project.clips).filter((clip) => wanted.has(clip.trackId));
}

// ---------------------------------------------------------------------------
// Splitting
// ---------------------------------------------------------------------------

export const splitAtRange = defineCommand<RangeTarget>({
  type: 'range.split',
  label: () => 'Split at range edges',
  parse: parseRange,
  apply: (project, p) => {
    // Cutting at both edges is what turns "a stretch of time" into "a set of
    // whole clips", which every other range operation then works on.
    let next = project;
    for (const tick of [p.startTicks, p.endTicks]) {
      const ids = clipsOfTracks(next, p.trackIds)
        .filter((clip) => clip.startTicks < tick && clipEndTicks(next, clip) > tick)
        .map((clip) => clip.id);
      if (ids.length === 0) continue;
      next = runSplit(next, ids, tick);
    }
    return next;
  },
});

/** Splits through the clip command so the arithmetic lives in one place. */
function runSplit(project: Project, clipIds: string[], tick: number): Project {
  const pieces: Clip[] = [];
  const shortened: Clip[] = [];

  for (const id of clipIds) {
    const clip = project.clips[id];
    if (!clip) continue;
    const head = sliceClip(project, clip, clip.startTicks, tick, clip.id);
    const tail = sliceClip(project, clip, tick, clipEndTicks(project, clip), newClipId());
    if (!head || !tail) continue;
    shortened.push(head);
    pieces.push(tail);
  }
  if (pieces.length === 0) return project;

  return insertClips(
    { ...project, clips: { ...project.clips, ...Object.fromEntries(shortened.map((c) => [c.id, c])) } },
    pieces,
  );
}

// ---------------------------------------------------------------------------
// Removing
// ---------------------------------------------------------------------------

/** Takes the material inside a range out of the named tracks. */
function clearRange(project: Project, target: RangeTarget): Project {
  const affected = clipsOfTracks(project, target.trackIds).filter(
    (clip) => clip.startTicks < target.endTicks && clipEndTicks(project, clip) > target.startTicks,
  );
  if (affected.length === 0) return project;

  const replacements: Clip[] = [];
  for (const clip of affected) {
    replacements.push(...subtractRange(project, clip, target.startTicks, target.endTicks, [newClipId(), newClipId()]));
  }

  const removed = removeClips(project, affected.map((clip) => clip.id));
  return insertClips(removed, replacements);
}

export const deleteRange = defineCommand<RangeTarget>({
  type: 'range.delete',
  label: () => 'Clear range',
  parse: parseRange,
  apply: (project, p) => clearRange(project, p),
});

// ---------------------------------------------------------------------------
// Shifting time
// ---------------------------------------------------------------------------

/**
 * Moves everything at or after `fromTicks` by `deltaTicks` on the named tracks.
 *
 * Automation and markers move with it. A ripple that shifted the audio but left
 * the volume curve and the "chorus" marker where they were would silently
 * de-synchronise the project — the edit would look right and sound wrong.
 */
function shiftAfter(project: Project, trackIds: readonly string[], fromTicks: number, deltaTicks: number, moveMarkers: boolean): Project {
  if (deltaTicks === 0) return project;
  const wanted = new Set(trackIds);

  const clips = { ...project.clips };
  for (const clip of Object.values(project.clips)) {
    if (!wanted.has(clip.trackId) || clip.startTicks < fromTicks) continue;
    clips[clip.id] = { ...clip, startTicks: Math.max(0, clip.startTicks + deltaTicks) };
  }

  const automation: Record<string, AutomationLane> = { ...project.automation };
  for (const lane of Object.values(project.automation)) {
    if (!wanted.has(lane.target.trackId)) continue;
    const points = lane.points.map((point) =>
      point.tick >= fromTicks ? { ...point, tick: Math.max(0, point.tick + deltaTicks) } : point,
    );
    automation[lane.id] = { ...lane, points: points.sort((a, b) => a.tick - b.tick) };
  }

  const markers: Marker[] = moveMarkers
    ? project.markers
        .map((marker) => (marker.tick >= fromTicks ? { ...marker, tick: Math.max(0, marker.tick + deltaTicks) } : marker))
        .sort((a, b) => a.tick - b.tick)
    : project.markers;

  const tracks = { ...project.tracks };
  for (const trackId of trackIds) {
    const track = tracks[trackId];
    if (!track) continue;
    tracks[trackId] = {
      ...track,
      clipIds: [...track.clipIds].sort((a, b) => (clips[a]?.startTicks ?? 0) - (clips[b]?.startTicks ?? 0)),
    };
  }

  return { ...project, clips, automation, markers, tracks };
}

export const spliceOut = defineCommand<RangeTarget & { allTracks: boolean }>({
  type: 'range.spliceOut',
  label: () => 'Splice out',
  parse: (raw) => {
    const target = parseRange(raw);
    return target ? { ...target, allTracks: boolParam(raw.allTracks, false) ?? false } : null;
  },
  apply: (project, p) => {
    const trackIds = p.allTracks ? [...project.trackOrder] : p.trackIds;
    const length = p.endTicks - p.startTicks;

    // Remove first, then close the gap. Doing it the other way round would
    // shift the very material that is about to be deleted.
    const cleared = clearRange(project, { ...p, trackIds });
    // Markers only move when the whole arrangement does; shifting them for a
    // splice on one track would put them out of step with everything else.
    return shiftAfter(cleared, trackIds, p.endTicks, -length, p.allTracks);
  },
});

export const insertTime = defineCommand<{
  trackIds: string[];
  startTicks: number;
  lengthTicks: number;
  allTracks: boolean;
}>({
  type: 'range.insert',
  label: () => 'Insert time',
  parse: (raw) => {
    const trackIds = idsParam(raw.trackIds, MAX_TRACKS);
    const startTicks = intParam(raw.startTicks, 0, Number.MAX_SAFE_INTEGER);
    const lengthTicks = intParam(raw.lengthTicks, 1, Number.MAX_SAFE_INTEGER);
    if (!trackIds || startTicks === null || lengthTicks === null) return null;
    return { trackIds, startTicks, lengthTicks, allTracks: boolParam(raw.allTracks, false) ?? false };
  },
  apply: (project, p) => {
    const trackIds = p.allTracks ? [...project.trackOrder] : p.trackIds;
    if (trackIds.length === 0) return project;

    // A clip straddling the insertion point is split, so the gap opens exactly
    // where it was asked for rather than pushing the whole clip aside.
    const straddling = clipsOfTracks(project, trackIds)
      .filter((clip) => clip.startTicks < p.startTicks && clipEndTicks(project, clip) > p.startTicks)
      .map((clip) => clip.id);
    const split = straddling.length > 0 ? runSplit(project, straddling, p.startTicks) : project;

    return shiftAfter(split, trackIds, p.startTicks, p.lengthTicks, p.allTracks);
  },
});

// ---------------------------------------------------------------------------
// Keeping and repeating
// ---------------------------------------------------------------------------

export const cropToRange = defineCommand<RangeTarget & { allTracks: boolean }>({
  type: 'range.crop',
  label: () => 'Crop to range',
  parse: (raw) => {
    const target = parseRange(raw);
    return target ? { ...target, allTracks: boolParam(raw.allTracks, true) ?? true } : null;
  },
  apply: (project, p) => {
    const trackIds = p.allTracks ? [...project.trackOrder] : p.trackIds;

    // Everything before and everything after, then slide the survivor to zero
    // so the piece starts where it now begins.
    let next = clearRange(project, { trackIds, startTicks: 0, endTicks: p.startTicks });
    next = clearRange(next, { trackIds, startTicks: p.endTicks, endTicks: Number.MAX_SAFE_INTEGER });
    return shiftAfter(next, trackIds, 0, -p.startTicks, p.allTracks);
  },
});

export const duplicateRange = defineCommand<RangeTarget & { allTracks: boolean; ripple: boolean }>({
  type: 'range.duplicate',
  label: () => 'Duplicate range',
  parse: (raw) => {
    const target = parseRange(raw);
    if (!target) return null;
    return {
      ...target,
      allTracks: boolParam(raw.allTracks, false) ?? false,
      ripple: boolParam(raw.ripple, true) ?? true,
    };
  },
  apply: (project, p) => {
    const trackIds = p.allTracks ? [...project.trackOrder] : p.trackIds;
    const length = p.endTicks - p.startTicks;

    // Take the copy before anything moves, so it is a copy of what was there.
    const copies: Clip[] = [];
    for (const clip of clipsOfTracks(project, trackIds)) {
      if (clip.startTicks >= p.endTicks || clipEndTicks(project, clip) <= p.startTicks) continue;
      const slice = sliceClip(project, clip, p.startTicks, p.endTicks, newClipId());
      if (slice) copies.push({ ...slice, startTicks: slice.startTicks + length });
    }
    if (copies.length === 0) return project;

    // Rippling makes room for the repeat; without it the copy lands on top of
    // whatever follows, which is occasionally what a user wants and never what
    // they expect.
    const base = p.ripple
      ? shiftAfter(project, trackIds, p.endTicks, length, p.allTracks)
      : clearRange(project, { trackIds, startTicks: p.endTicks, endTicks: p.endTicks + length });

    return insertClips(base, copies);
  },
});

/** Copies a range's material out of a project, for pasting elsewhere. */
/**
 * Pastes a stretch of time, previously copied, onto a set of tracks.
 *
 * The material carries a **track index** rather than a track id: what was copied
 * from the first, second and third tracks of a selection lands on the first,
 * second and third tracks of wherever it is pasted. Pasting by id would refuse
 * to move a chorus from the verse tracks to another set, which is most of the
 * reason to have a time clipboard at all.
 *
 * `replace` clears what is already there before dropping the material in;
 * `insert` opens a gap of the same length and pushes everything after it along.
 */
export interface CopiedAutomation {
  /** Index of the track within the copied selection. */
  trackIndex: number;
  /** Which parameter the points belong to, so the lane can be found or made. */
  kind: AutomationTarget['kind'];
  effectId: string | null;
  paramId: string;
  points: { tick: number; value: number; curve: AutomationCurve }[];
}

export const pasteRange = defineCommand<{
  trackIds: string[];
  startTicks: number;
  lengthTicks: number;
  mode: 'replace' | 'insert';
  clips: Clip[];
  automation: CopiedAutomation[];
}>({
  type: 'range.paste',
  label: (p) => (p.mode === 'insert' ? 'Paste and insert' : 'Paste over range'),
  parse: (raw) => {
    const trackIds = idsParam(raw.trackIds, MAX_TRACKS);
    const startTicks = intParam(raw.startTicks, 0, Number.MAX_SAFE_INTEGER);
    const lengthTicks = intParam(raw.lengthTicks, 1, Number.MAX_SAFE_INTEGER);
    const mode = enumParam(raw.mode, ['replace', 'insert'] as const, 'replace');
    if (!trackIds || trackIds.length === 0 || startTicks === null || lengthTicks === null || !mode) return null;
    if (!Array.isArray(raw.clips)) return null;

    // Clips arrive as whole objects because they came out of `copyRange`, which
    // has already validated and rebased them. They are checked again anyway: a
    // command is a boundary, and material that arrived from a file or from
    // somewhere else entirely has to be treated the same as material from here.
    const clips: Clip[] = [];
    for (const entry of raw.clips) {
      const clip = parseClipForPaste(entry);
      if (!clip) return null;
      clips.push(clip);
    }
    if (clips.length > 5000) return null;

    const automation: CopiedAutomation[] = [];
    for (const entry of Array.isArray(raw.automation) ? raw.automation : []) {
      const lane = parseCopiedAutomation(entry);
      if (!lane) return null;
      automation.push(lane);
    }
    return { trackIds, startTicks, lengthTicks, mode, clips, automation };
  },
  apply: (project, p) => {
    // Automation alone is a paste worth doing: a curve copied without any clips
    // under it is still material.
    if (p.clips.length === 0 && p.automation.length === 0) return project;
    const targets = p.trackIds.filter((id) => project.tracks[id]);
    if (targets.length === 0) return project;

    const prepared =
      p.mode === 'insert'
        ? runCommandInsert(project, targets, p.startTicks, p.lengthTicks)
        : clearRange(project, { trackIds: targets, startTicks: p.startTicks, endTicks: p.startTicks + p.lengthTicks });

    const placed: Clip[] = [];
    for (const clip of p.clips) {
      // The index was stored in the clip's track id as it left the clipboard;
      // anything out of range is dropped rather than piled onto the last track.
      const index = Number(clip.trackId);
      const trackId = targets[index];
      if (!Number.isInteger(index) || !trackId) continue;
      placed.push({
        ...clip,
        id: newClipId(),
        trackId,
        startTicks: p.startTicks + clip.startTicks,
      } as Clip);
    }
    const withClips = placed.length === 0 ? prepared : insertClips(prepared, placed);
    return pasteAutomation(withClips, targets, p.startTicks, p.lengthTicks, p.automation);
  },
});

/**
 * Puts copied curves back, into the lane each parameter already has or a new one.
 *
 * The points inside the pasted stretch are replaced rather than merged with, for
 * the same reason a recorded pass replaces what it covers: pasting a chorus over
 * a verse means the chorus's shape, not the two of them added together.
 */
function pasteAutomation(
  project: Project,
  targets: readonly string[],
  startTicks: number,
  lengthTicks: number,
  lanes: readonly CopiedAutomation[],
): Project {
  if (lanes.length === 0) return project;
  let next = project;

  for (const copied of lanes) {
    const trackId = targets[copied.trackIndex];
    const track = next.tracks[trackId];
    if (!track) continue;

    let laneId = track.automationLaneIds.find((id) => {
      const lane = next.automation[id];
      return (
        lane &&
        lane.target.kind === copied.kind &&
        lane.target.paramId === copied.paramId &&
        lane.target.effectId === copied.effectId
      );
    });

    if (!laneId) {
      const target: AutomationTarget = {
        kind: copied.kind,
        trackId,
        effectId: copied.effectId,
        paramId: copied.paramId,
      };
      // A lane for a parameter this track does not have is skipped rather than
      // invented: pasting a filter curve onto a track with no filter would
      // create a control that governs nothing.
      if (!resolveParamDescriptor(next, target)) continue;
      laneId = newLaneId();
      next = {
        ...next,
        automation: {
          ...next.automation,
          [laneId]: { id: laneId, target, enabled: true, height: 62, points: [] },
        },
        tracks: {
          ...next.tracks,
          [trackId]: { ...track, automationLaneIds: [...track.automationLaneIds, laneId] },
        },
      };
    }

    const lane = next.automation[laneId];
    if (!lane) continue;
    const from = startTicks;
    const to = startTicks + lengthTicks;
    const kept = lane.points.filter((point) => point.tick < from || point.tick > to);
    const written = copied.points.map((point) => ({
      id: newPointId(),
      tick: from + point.tick,
      value: point.value,
      curve: point.curve,
    }));
    next = {
      ...next,
      automation: {
        ...next.automation,
        [laneId]: {
          ...lane,
          points: [...kept, ...written].sort((a, b) => a.tick - b.tick || a.id.localeCompare(b.id)),
        },
      },
    };
  }
  return next;
}

/** One lane's worth of copied points, read defensively. */
function parseCopiedAutomation(raw: unknown): CopiedAutomation | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Record<string, unknown>;
  const trackIndex = intParam(record.trackIndex, 0, MAX_TRACKS);
  const kind = enumParam(record.kind, ['track', 'effect', 'instrument'] as const);
  const paramId = typeof record.paramId === 'string' ? record.paramId.slice(0, 64) : null;
  if (trackIndex === null || !kind || !paramId) return null;
  const effectId = record.effectId === null || record.effectId === undefined ? null : String(record.effectId);

  const points: CopiedAutomation['points'] = [];
  for (const entry of Array.isArray(record.points) ? record.points : []) {
    if (typeof entry !== 'object' || entry === null) return null;
    const point = entry as Record<string, unknown>;
    const tick = intParam(point.tick, 0, Number.MAX_SAFE_INTEGER);
    const value = numberParam(point.value, 0, 1);
    const curve = enumParam(point.curve, ['linear', 'hold', 'smooth'] as const, 'linear');
    if (tick === null || value === null || !curve) return null;
    points.push({ tick, value, curve });
  }
  return { trackIndex, kind, effectId, paramId, points };
}

/**
 * The automation inside a range, rebased to its start and keyed by track index.
 *
 * Copying a stretch of time that leaves its curves behind is a trap: the clips
 * arrive somewhere new and play at whatever level the destination happened to be
 * automated to, which sounds like a bug in the paste rather than a missing
 * feature.
 */
export function copyRangeAutomation(project: Project, target: RangeTarget): CopiedAutomation[] {
  const order = new Map(target.trackIds.map((id, index) => [id, index]));
  const out: CopiedAutomation[] = [];

  for (const trackId of target.trackIds) {
    const track = project.tracks[trackId];
    if (!track) continue;
    for (const laneId of track.automationLaneIds) {
      const lane = project.automation[laneId];
      if (!lane) continue;
      const points = lane.points
        .filter((point) => point.tick >= target.startTicks && point.tick <= target.endTicks)
        .map((point) => ({
          tick: point.tick - target.startTicks,
          value: point.value,
          curve: point.curve,
        }));
      if (points.length === 0) continue;
      out.push({
        trackIndex: order.get(trackId) ?? 0,
        kind: lane.target.kind,
        effectId: lane.target.effectId,
        paramId: lane.target.paramId,
        points,
      });
    }
  }
  return out;
}

/**
 * A clip from the clipboard, with its track index kept in place of a track id.
 *
 * The project parser does the reading, so there is one definition of what a
 * valid clip is; only the track field differs, because on the clipboard it holds
 * a position in the copied selection rather than a track that exists.
 */
function parseClipForPaste(raw: unknown): Clip | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const record = raw as Record<string, unknown>;
  const index = Number(record.trackId);
  if (!Number.isInteger(index) || index < 0 || index > MAX_TRACKS) return null;
  const clip = parseClipValue({ ...record, trackId: 'trk_clipboard' });
  return clip ? ({ ...clip, trackId: String(index) } as Clip) : null;
}

/** Opens a gap, exactly as `range.insert` does, without going through the registry. */
function runCommandInsert(
  project: Project,
  trackIds: readonly string[],
  startTicks: number,
  lengthTicks: number,
): Project {
  const straddling = clipsOfTracks(project, trackIds)
    .filter((clip) => clip.startTicks < startTicks && clipEndTicks(project, clip) > startTicks)
    .map((clip) => clip.id);
  const split = straddling.length > 0 ? runSplit(project, straddling, startTicks) : project;
  return shiftAfter(split, trackIds, startTicks, lengthTicks, false);
}

/**
 * Material on the clipboard, with its track index in place of a track id.
 *
 * Copying keeps the shape of the selection: the first track of the range becomes
 * index 0, the second index 1, and so on, so pasting somewhere else lands the
 * parts in the same order relative to each other.
 */
export function copyRangeForPaste(project: Project, target: RangeTarget): Clip[] {
  const order = new Map(target.trackIds.map((id, index) => [id, index]));
  return copyRange(project, target).map((clip) => ({
    ...clip,
    trackId: String(order.get(clip.trackId) ?? 0),
  }));
}

export function copyRange(project: Project, target: RangeTarget): Clip[] {
  const out: Clip[] = [];
  for (const clip of clipsOfTracks(project, target.trackIds)) {
    if (clip.startTicks >= target.endTicks || clipEndTicks(project, clip) <= target.startTicks) continue;
    const slice = sliceClip(project, clip, target.startTicks, target.endTicks, newClipId());
    if (slice) out.push({ ...slice, startTicks: slice.startTicks - target.startTicks });
  }
  return out.sort((a, b) => a.startTicks - b.startTicks);
}
